import { Injectable, ApplicationRef, Inject } from '@angular/core';
import { interval, concat, Observable } from 'rxjs';
import { first } from 'rxjs/operators';

import { ErrorHandler } from './error-handler.service';
import { HttpClientService } from './http-client.service';
import { WINDOW } from '../factory/window.factory';

@Injectable({
  providedIn: 'root',
})
export class ReleaseUpdateService {
  interval = 10 * 60 * 1000; // 10 minutes
  baseUrl: string;
  etag: string;
  newEtag: string;

  constructor(
    private readonly appRef: ApplicationRef,
    private readonly httpClientService: HttpClientService,
    @Inject(WINDOW) private readonly window: Window,
    private readonly errorHandler: ErrorHandler,
  ) {
    this.baseUrl = this.window.location.origin;
    this.getLastVersion().subscribe(
      (res) => {
        this.etag = this.parseEtag(res.headers.get('etag'));
        this.checkForUpdates();
      },
      (error) => this.handleErrorForDebug(error),
    );
  }

  private checkForUpdates() {
    const appIsStable$ = this.appRef.isStable.pipe(first((isStable) => isStable === true));
    const interval$ = interval(this.interval);
    const intervalOnceAppIsStable$ = concat(appIsStable$, interval$);
    intervalOnceAppIsStable$.subscribe(() => this.retrieveLastEtag());
  }

  private retrieveLastEtag() {
    this.getLastVersion().subscribe(
      (res) => {
        this.newEtag = this.parseEtag(res.headers.get('etag'));
      },
      (error) => this.handleErrorForDebug(error),
    );
  }

  private parseEtag(etag: string): string {
    // eslint-disable-next-line prefer-regex-literals
    const re = new RegExp('^W/');
    const isWeakEtag = re.test(etag);

    return isWeakEtag ? etag.substring(2) : etag;
  }

  private getLastVersion(): Observable<any> {
    return this.httpClientService.head(this.baseUrl, '/index.html', { observe: 'response' });
  }

  updateAvailable(): boolean {
    if (!this.etag || !this.newEtag) {
      return false;
    }

    return this.etag.localeCompare(this.newEtag) !== 0;
  }

  private handleErrorForDebug(error: string) {
    this.errorHandler.handleForDebug(error, {
      service: 'ReleaseUpdateService',
      etag: this.etag,
      newEtag: this.newEtag,
    });
  }
}
